import { Col, Row, Spinner } from "react-bootstrap";
import {
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  SelectChangeEvent,
  TextField,
  Typography
} from "@mui/material";
import {
  ExtendedRepositoryObject,
  RepositoryObject,
  ValidityStatus
} from "../../backend/types";
import { useContext, useEffect, useState } from "react";
import { FileSelectModal } from "../abstractComponents/fileSelectModal";
import { FileType } from "../abstractComponents/fileTreeView";
import {
  compressedFileExtensions,
  excelFormatsExtensions,
  imageExtensions,
  installersExtensions,
  isFileEndsWith,
  moviesExtensions
} from "../../helpers/fileHelper";
import { getKeyByValue } from "../../helpers/stringHelper";
import { useRepositoryObject } from "../../backend/hooks/pmxActivity/queryGetRepositoryObject";
import { useRepositoryObjects } from "../../backend/hooks/pmxActivity/queryGetRepositoryObjects";
import { PmxActivityDetailsContext } from "../../contexts/pmxActivityDetailsProvider";

export class FileObjectState {
  versionId!: string;
  repoId!: number;
  name!: string;
  allVersions!: RepositoryObject[];
}

interface FileSelectProps {
  identifier: string;
  fileObject: FileObjectState;
  updateFileObject: (identifier: string, fileObject: FileObjectState) => void;
}

export function FileSelect(props: FileSelectProps) {
  const { activityData } = useContext(PmxActivityDetailsContext);

  const [modalConfig, setModalConfig] = useState<{
    open: boolean;
    fileType?: FileType;
    text?: string | any;
  }>({
    open: false
  });

  const [temporaryValue, setTemporaryValue] = useState<any>();
  const updateFileSelectionTemporary = (fileType: FileType, node: any) => {
    if (fileType === FileType.CompareView) {
      const absolutePathWithoutRepoName = node.absolutePath
        .split("/")
        .slice(1)
        .join("/");
      setTemporaryValue({
        repoId: node.repositoryId,
        versionId: node.versionId,
        absolutePath: absolutePathWithoutRepoName
      });
    }
  };

  const { callSingleRepositoryObject } = useRepositoryObject();

  const { callRepositoryObjects, isGetRepositoryObjectsLoading } =
    useRepositoryObjects(activityData?.mainRepository?.id, false);

  const isFileHidden = (file: ExtendedRepositoryObject) => {
    const excludedExtensions = [
      imageExtensions,
      compressedFileExtensions,
      moviesExtensions,
      installersExtensions,
      excelFormatsExtensions
    ].flat();
    excludedExtensions.push(".pdf");
    return (
      file.absolutePath.endsWith("/") ||
      isFileEndsWith(file.name, excludedExtensions)
    );
  };

  const updateInput = (event: SelectChangeEvent) => {
    props.updateFileObject(props.identifier, {
      ...props.fileObject,
      versionId: event.target.value
    });
  };

  const openModal = (identifier: string) => {
    setModalConfig({
      open: true,
      fileType: FileType.CompareView,
      text: `Select ${identifier} file`
    });
  };

  const onConfirmModal = () => {
    if (temporaryValue.versionId !== props.fileObject.versionId) {
      updateFileVersion();
    }
  };

  type FetchAndUpdateFileObjectArgs = {
    repoId: string;
    versionId?: string;
    name?: string;
    location?: string;
  };

  const extractNameAndLocation = (path: string) => {
    const locationArr = path.split("/");
    const name = locationArr.pop() || "";
    const location = locationArr.length > 0 ? locationArr.join("/") + "/" : "";
    return { name, location };
  };

  const fetchAndUpdateFileObject = async ({
    repoId,
    versionId
  }: FetchAndUpdateFileObjectArgs) => {
    const { data: singleData } = await callSingleRepositoryObject({
      variables: { versionId }
    });
    const repositoryObject = singleData.getRepositoryObject.repositoryObject;
    const { name, location } = extractNameAndLocation(repositoryObject.name);

    const { data: objectsData } = await callRepositoryObjects({
      variables: {
        repoId,
        ...(name ? { name } : {}),
        ...(location ? { location } : {}),
        versioned: true
      }
    });
    const repositoryObjects =
      objectsData.getRepositoryObjects.repositoryObjects;
    props.updateFileObject(props.identifier, {
      repoId: repositoryObject.repository.id,
      name: repositoryObject.name,
      versionId: repositoryObject.versionId,
      allVersions: repositoryObjects
    });
  };

  const updateFileVersion = async () => {
    const { name, location } = extractNameAndLocation(
      temporaryValue.absolutePath
    );

    const { data: objectsData } = await callRepositoryObjects({
      variables: {
        repoId: temporaryValue.repoId,
        ...(name ? { name } : {}),
        ...(location ? { location } : {}),
        versioned: true
      }
    });
    const repositoryObjects =
      objectsData.getRepositoryObjects.repositoryObjects;
    props.updateFileObject(props.identifier, {
      repoId: temporaryValue.repoId,
      name: temporaryValue.absolutePath,
      versionId: temporaryValue.versionId,
      allVersions: repositoryObjects
    });
  };

  useEffect(() => {
    fetchAndUpdateFileObject({
      repoId: String(props.fileObject.repoId),
      versionId: props.fileObject.versionId
    });
  }, []);

  const fileVersions = props.fileObject;
  return (
    <>
      <Col>
        <Col>
          <h5>{props.identifier}</h5>
        </Col>
        {isGetRepositoryObjectsLoading ? (
          <div className="center_div">
            <br />
            <Spinner animation="border" className="spinner_color" />
            <p>Loading...</p>
          </div>
        ) : (
          <>
            <Row className="my-4">
              <TextField
                id={`${props.identifier.toLowerCase()}-file`}
                label={`${props.identifier} File`}
                multiline
                aria-readonly={true}
                value={props.fileObject.name}
                onClick={() => openModal(props.identifier)}
              />
            </Row>
            <Row>
              {fileVersions && (
                <FormControl>
                  <InputLabel>Versions</InputLabel>
                  <Select
                    id={`${props.identifier.toLowerCase()}-version`}
                    defaultValue={""}
                    value={
                      props.fileObject.allVersions.length > 0
                        ? props.fileObject.versionId
                        : ""
                    }
                    label="Version"
                    onChange={(event: SelectChangeEvent) => {
                      updateInput(event);
                    }}
                  >
                    {fileVersions?.allVersions?.map(
                      (file: RepositoryObject) => (
                        <MenuItem
                          value={`${file.versionId}`}
                          key={`${props.identifier}-${file.versionId}`}
                          id={`${props.identifier.toLowerCase()}-revision-${
                            file.revision
                          }`}
                        >
                          <Row id={`row-version-${file.versionId}`}>
                            <Typography
                              variant="button"
                              display="block"
                              sx={{ width: "200px" }}
                            >
                              Revision: {file.revision}
                            </Typography>
                            <div className="d-flex gap-4 fs-12px">
                              <p className="mb-0">
                                Created at: {file.createdAt}
                              </p>
                              <p className="mb-0">
                                Created by: {file.createdBy}
                              </p>
                              <p className="mb-0">Size: {file.size} KB</p>
                              <p className="mb-0">
                                Validity:{" "}
                                {file.validity?.overallValidityStatus &&
                                  getKeyByValue(
                                    ValidityStatus,
                                    file.validity.overallValidityStatus
                                  )}
                              </p>
                            </div>
                          </Row>
                        </MenuItem>
                      )
                    )}
                  </Select>
                </FormControl>
              )}
            </Row>
          </>
        )}
      </Col>
      <FileSelectModal
        setModalAction={setModalConfig}
        updateFileSelection={updateFileSelectionTemporary}
        onConfirmModal={onConfirmModal}
        modelConfig={modalConfig}
        selectedFile={props.fileObject.name}
        isFileHidden={isFileHidden}
        treeSelection={[]}
      />
    </>
  );
}
